/**
 * A played file heard as a listener hears it: attacks in seconds, and how strongly each one
 * marks the beat.
 *
 * This is the only half of beat finding that reads notes. Everything after it works on the
 * attacks alone, which is what lets the beat tracker be stated without a word about keys,
 * tracks or channels. Keys struck within a few hundredths of a second are one attack -- a
 * pianist's chord is never quite together, and hearing it as three would put three beats
 * where there is one.
 */

import type { MidiFile } from "./read";

/** Keys struck closer together than this, in seconds, are one attack. */
const TOGETHER = 0.035;

/** One attack: when it came, how much it weighs, and what it struck. */
export interface Attack {
  readonly time: number;
  /** More keys, struck harder, weigh more. */
  readonly weight: number;
  /** The longest note begun here, in seconds. */
  readonly held: number;
}

/** One note of the file, timed in seconds. */
export interface TimedNote {
  readonly track: number;
  readonly index: number;
  readonly key: number;
  readonly velocity: number;
  readonly start: number;
  readonly end: number;
  readonly drum: boolean;
}

/** Seconds at a tick, from the file's tempo marks: 120 a minute until the file says else. */
export function secondsOf(file: MidiFile): (tick: number) => number {
  const division = file.division > 0 ? file.division : 480;
  const marks = [...file.tempos].sort((first, second) => first.tick - second.tick);
  const starts: { tick: number; seconds: number; perTick: number }[] = [
    { tick: 0, seconds: 0, perTick: 0.5 / division },
  ];
  for (const mark of marks) {
    const last = starts[starts.length - 1] as (typeof starts)[number];
    const seconds = last.seconds + (mark.tick - last.tick) * last.perTick;
    const perTick =
      mark.microsPerQuarter > 0 ? mark.microsPerQuarter / 1e6 / division : last.perTick;
    if (mark.tick === last.tick) starts[starts.length - 1] = { ...last, perTick };
    else starts.push({ tick: mark.tick, seconds, perTick });
  }
  return (tick) => {
    let at = starts[0] as (typeof starts)[number];
    for (const start of starts) {
      if (start.tick > tick) break;
      at = start;
    }
    return at.seconds + (tick - at.tick) * at.perTick;
  };
}

/** Every note of the file in seconds, remembering where it came from. */
export function timedNotes(file: MidiFile): TimedNote[] {
  const seconds = secondsOf(file);
  return file.tracks.flatMap((track, trackIndex) =>
    track.notes.map((note, index) => ({
      track: trackIndex,
      index,
      key: note.key,
      velocity: note.velocity,
      start: seconds(note.start),
      end: seconds(note.start + note.ticks),
      drum: note.channel === 9,
    })),
  );
}

/** The attacks of a performance, earliest first. */
export function attacksOf(notes: readonly TimedNote[]): Attack[] {
  const sorted = [...notes].sort((first, second) => first.start - second.start);
  const attacks: Attack[] = [];
  let group: TimedNote[] = [];
  const close = (): void => {
    const first = group[0];
    if (first === undefined) return;
    attacks.push({
      time: first.start,
      weight: group.reduce((sum, note) => sum + 0.5 + note.velocity / 127, 0),
      held: Math.max(...group.map((note) => note.end - note.start)),
    });
    group = [];
  };
  for (const note of sorted) {
    const first = group[0];
    if (first !== undefined && note.start - first.start > TOGETHER) close();
    group.push(note);
  }
  close();
  return attacks;
}
